import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../services/cloudflareService';
import {
    FileText, CreditCard, DollarSign, Calendar, CheckCircle2, Clock,
    AlertTriangle, Download, ChevronDown, ChevronRight, Receipt,
    Building2, Shield, RefreshCw, TrendingUp, Wallet, Eye
} from 'lucide-react';

interface ClientBillingViewProps {
    theme: 'dark' | 'light';
    groupId: string;
    groupName: string;
}

interface BillingAccount {
    hasAccount: boolean;
    client: {
        id: string;
        businessName: string;
        tradeName: string;
        rfc: string;
        currency: string;
        contactName: string;
        contactEmail: string;
    } | null;
    contract: {
        id: string;
        name: string;
        tier: string;
        billingCycle: string;
        baseAmount: number;
        discountPercent: number;
        discountReason: string;
        finalAmount: number;
        currency: string;
        startDate: string;
        endDate: string;
        autoRenew: number;
        status: string;
        hospitalsIncluded: number;
    } | null;
    invoices: any[];
    payments: any[];
    balance: {
        totalBilled: number;
        totalPaid: number;
        outstanding: number;
    };
}

const tierLabels: Record<string, string> = {
    clinic: 'Clínica',
    hospital: 'Hospital',
    chain: 'Cadena Corporativa',
};

const cycleLabels: Record<string, string> = {
    monthly: 'Mensual',
    quarterly: 'Trimestral',
    annual: 'Anual',
};

const statusConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
    paid: { label: 'Pagada', color: 'emerald', icon: CheckCircle2 },
    pending: { label: 'Pendiente', color: 'amber', icon: Clock },
    sent: { label: 'Enviada', color: 'blue', icon: FileText },
    partial: { label: 'Pago Parcial', color: 'amber', icon: Clock },
    overdue: { label: 'Vencida', color: 'red', icon: AlertTriangle },
    cancelled: { label: 'Cancelada', color: 'slate', icon: AlertTriangle },
};

const ClientBillingView: React.FC<ClientBillingViewProps> = ({ theme, groupId, groupName }) => {
    const [account, setAccount] = useState<BillingAccount | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState<'overview' | 'invoices' | 'payments'>('overview');
    const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);

    const t = useMemo(() => ({
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMain: theme === 'dark' ? 'text-slate-300' : 'text-slate-600',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        cardShadow: theme === 'dark' ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]' : 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
        innerBg: theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-slate-50 border border-slate-100',
        hoverBg: theme === 'dark' ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-50',
        divider: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
    }), [theme]);

    const formatCurrency = (amount: number, currency: string = 'MXN') => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-MX', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    useEffect(() => {
        const fetchAccount = async () => {
            setLoading(true);
            try {
                const res = await apiFetch(`/api/billing/my-account/${groupId}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setAccount(data);
                }
            } catch (err) {
                console.error('Error fetching billing account:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAccount();
    }, [groupId]);

    const cardClass = `${t.cardBg} rounded-2xl border ${t.cardBorder} ${t.cardShadow} p-6 relative overflow-hidden`;
    const currency = account?.client?.currency || 'MXN';

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    // No billing account yet
    if (!account?.hasAccount) {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className={`text-xl font-black ${t.textWhite} tracking-tight flex items-center gap-3`}>
                        <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20">
                            <Wallet className="w-5 h-5" />
                        </div>
                        Mi Cuenta
                    </h2>
                    <p className={`text-xs ${t.textMuted} font-medium mt-2`}>Facturación y estado de cuenta</p>
                </div>

                <div className={`${cardClass} flex flex-col items-center justify-center py-16 text-center`}>
                    <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'} mb-5`}>
                        <Shield className="w-10 h-10 text-indigo-400" />
                    </div>
                    <h3 className={`text-lg font-bold ${t.textWhite} mb-2`}>Cuenta en proceso de configuración</h3>
                    <p className={`text-sm ${t.textMuted} max-w-md`}>
                        Tu cuenta de facturación está siendo configurada por el equipo de Hospital Status. 
                        Pronto podrás ver aquí tu contrato, facturas y estado de cuenta.
                    </p>
                    <p className={`text-xs ${t.textMuted} mt-4`}>
                        ¿Tienes dudas? Contacta a <span className="text-indigo-400 font-bold">soporte@hospital-status.com</span>
                    </p>
                </div>
            </div>
        );
    }

    const { client, contract, invoices, payments, balance } = account;

    // Calculate overdue invoices
    const now = new Date().toISOString().split('T')[0];
    const overdueInvoices = invoices.filter((inv: any) => 
        (inv.status === 'pending' || inv.status === 'sent' || inv.status === 'partial') && inv.dueDate < now
    );
    const pendingInvoices = invoices.filter((inv: any) => 
        inv.status === 'pending' || inv.status === 'sent' || inv.status === 'partial'
    );

    const tabButtons = [
        { id: 'overview', label: 'Resumen' },
        { id: 'invoices', label: `Facturas (${invoices.length})` },
        { id: 'payments', label: `Pagos (${payments.length})` },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h2 className={`text-xl font-black ${t.textWhite} tracking-tight flex items-center gap-3`}>
                        <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20">
                            <Wallet className="w-5 h-5" />
                        </div>
                        Mi Cuenta
                    </h2>
                    <p className={`text-xs ${t.textMuted} font-medium mt-2`}>
                        {client!.businessName} {client!.rfc ? `• RFC: ${client!.rfc}` : ''}
                    </p>
                </div>

                {/* Sub-tabs */}
                <div className={`flex ${t.cardBg} p-1 rounded-xl border ${t.cardBorder}`}>
                    {tabButtons.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveView(tab.id as any)}
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                                activeView === tab.id
                                    ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]'
                                    : `${t.textMuted} ${t.hoverBg}`
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── OVERVIEW ── */}
            {activeView === 'overview' && (
                <div className="space-y-6 animate-fade-in">
                    {/* Balance Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className={cardClass}>
                            <div className={`p-2 rounded-lg w-fit mb-3 ${theme === 'dark' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                                <Receipt className="w-5 h-5" />
                            </div>
                            <p className={`text-2xl font-black ${t.textWhite} font-mono tracking-tight`}>
                                {formatCurrency(balance.totalBilled, currency)}
                            </p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Total Facturado</p>
                        </div>
                        <div className={cardClass}>
                            <div className={`p-2 rounded-lg w-fit mb-3 ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>
                                <CheckCircle2 className="w-5 h-5" />
                            </div>
                            <p className={`text-2xl font-black text-emerald-400 font-mono tracking-tight`}>
                                {formatCurrency(balance.totalPaid, currency)}
                            </p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Total Pagado</p>
                        </div>
                        <div className={cardClass}>
                            <div className={`p-2 rounded-lg w-fit mb-3 ${
                                balance.outstanding > 0
                                    ? (theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-200')
                                    : (theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200')
                            }`}>
                                <DollarSign className="w-5 h-5" />
                            </div>
                            <p className={`text-2xl font-black ${balance.outstanding > 0 ? 'text-amber-400' : 'text-emerald-400'} font-mono tracking-tight`}>
                                {formatCurrency(balance.outstanding, currency)}
                            </p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Saldo Pendiente</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Contract Card */}
                        <div className={cardClass}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-5 flex items-center gap-2`}>
                                <FileText className="w-4 h-4 text-indigo-400" /> Mi Contrato
                            </h3>
                            {contract ? (
                                <div className="space-y-4">
                                    <div>
                                        <p className={`text-lg font-bold ${t.textWhite}`}>{contract.name}</p>
                                        <p className={`text-xs ${t.textMuted} mt-1`}>
                                            Plan {tierLabels[contract.tier] || contract.tier} • {cycleLabels[contract.billingCycle] || contract.billingCycle}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className={`${t.innerBg} p-3 rounded-xl`}>
                                            <p className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-wider mb-0.5`}>Monto</p>
                                            <p className={`font-mono font-bold ${t.textWhite} text-lg`}>
                                                {formatCurrency(contract.finalAmount, contract.currency)}
                                            </p>
                                            {contract.discountPercent > 0 && (
                                                <p className="text-[10px] text-emerald-400 font-bold mt-0.5">
                                                    -{contract.discountPercent}% desc. aplicado
                                                </p>
                                            )}
                                        </div>
                                        <div className={`${t.innerBg} p-3 rounded-xl`}>
                                            <p className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-wider mb-0.5`}>Vigencia</p>
                                            <p className={`text-sm font-bold ${t.textWhite}`}>
                                                {formatDate(contract.startDate)}
                                            </p>
                                            <p className={`text-xs ${t.textMuted}`}>
                                                al {formatDate(contract.endDate)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className={`${t.innerBg} p-3 rounded-xl`}>
                                            <p className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-wider mb-0.5`}>Hospitales</p>
                                            <p className={`font-mono font-bold ${t.textWhite} text-lg`}>{contract.hospitalsIncluded}</p>
                                        </div>
                                        <div className={`${t.innerBg} p-3 rounded-xl`}>
                                            <p className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-wider mb-0.5`}>Renovación</p>
                                            <p className={`text-sm font-bold ${contract.autoRenew ? 'text-emerald-400' : t.textWhite}`}>
                                                {contract.autoRenew ? '✓ Automática' : 'Manual'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className={`text-sm ${t.textMuted} text-center py-8`}>Sin contrato activo</p>
                            )}
                        </div>

                        {/* Alerts + Quick Status */}
                        <div className={cardClass}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-5 flex items-center gap-2`}>
                                <AlertTriangle className="w-4 h-4 text-amber-400" /> Estado
                            </h3>
                            <div className="space-y-3">
                                {overdueInvoices.length > 0 && (
                                    <div className={`flex items-center gap-3 p-4 rounded-xl ${theme === 'dark' ? 'bg-red-500/5 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
                                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                        <div>
                                            <p className={`text-sm font-bold ${t.textWhite}`}>
                                                {overdueInvoices.length} factura{overdueInvoices.length > 1 ? 's' : ''} vencida{overdueInvoices.length > 1 ? 's' : ''}
                                            </p>
                                            <p className={`text-xs ${t.textMuted}`}>Favor de regularizar su pago</p>
                                        </div>
                                    </div>
                                )}

                                {pendingInvoices.length > 0 && overdueInvoices.length === 0 && (
                                    <div className={`flex items-center gap-3 p-4 rounded-xl ${theme === 'dark' ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                                        <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                                        <div>
                                            <p className={`text-sm font-bold ${t.textWhite}`}>
                                                {pendingInvoices.length} factura{pendingInvoices.length > 1 ? 's' : ''} por pagar
                                            </p>
                                            <p className={`text-xs ${t.textMuted}`}>
                                                {formatCurrency(pendingInvoices.reduce((sum: number, inv: any) => sum + (inv.total - (inv.paidAmount || 0)), 0), currency)} pendiente
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {pendingInvoices.length === 0 && overdueInvoices.length === 0 && (
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                        <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-3" />
                                        <p className={`text-sm font-bold ${t.textWhite}`}>Cuenta al corriente</p>
                                        <p className={`text-xs ${t.textMuted} mt-1`}>No tienes pagos pendientes</p>
                                    </div>
                                )}

                                {/* Last payment */}
                                {payments.length > 0 && (
                                    <div className={`${t.innerBg} p-4 rounded-xl`}>
                                        <p className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-wider mb-2`}>Último Pago Registrado</p>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className={`text-sm font-bold ${t.textWhite}`}>
                                                    {formatCurrency(payments[0].amount, payments[0].currency)}
                                                </p>
                                                <p className={`text-xs ${t.textMuted}`}>{formatDate(payments[0].paymentDate)}</p>
                                            </div>
                                            <p className={`text-xs font-mono ${t.textMuted}`}>{payments[0].invoiceNumber}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── INVOICES ── */}
            {activeView === 'invoices' && (
                <div className="space-y-4 animate-fade-in">
                    {invoices.length === 0 ? (
                        <div className={`${cardClass} text-center py-12`}>
                            <FileText className={`w-10 h-10 ${t.textMuted} mx-auto mb-3`} />
                            <p className={`text-sm font-bold ${t.textWhite}`}>Sin facturas todavía</p>
                            <p className={`text-xs ${t.textMuted} mt-1`}>Las facturas emitidas aparecerán aquí</p>
                        </div>
                    ) : (
                        invoices.map((inv: any) => {
                            const config = statusConfig[inv.status] || statusConfig.pending;
                            const isExpanded = expandedInvoice === inv.id;
                            const invoicePayments = payments.filter((p: any) => p.invoiceId === inv.id);

                            return (
                                <div key={inv.id} className={`${cardClass} !p-0`}>
                                    <button
                                        onClick={() => setExpandedInvoice(isExpanded ? null : inv.id)}
                                        className={`w-full p-5 text-left flex items-center gap-4 ${t.hoverBg} transition-colors rounded-2xl`}
                                    >
                                        <div className={`p-2 rounded-lg ${theme === 'dark' ? `bg-${config.color}-500/10 text-${config.color}-400 border border-${config.color}-500/20` : `bg-${config.color}-50 text-${config.color}-600 border border-${config.color}-200`}`}>
                                            <config.icon className="w-4 h-4" />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <p className={`text-sm font-bold ${t.textWhite}`}>{inv.invoiceNumber}</p>
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-${config.color}-500/10 text-${config.color}-400 border border-${config.color}-500/20`}>
                                                    {config.label}
                                                </span>
                                            </div>
                                            <p className={`text-xs ${t.textMuted}`}>
                                                {inv.periodLabel || `${formatDate(inv.periodStart)} — ${formatDate(inv.periodEnd)}`}
                                            </p>
                                        </div>

                                        <div className="text-right flex-shrink-0">
                                            <p className={`text-lg font-black font-mono ${t.textWhite}`}>
                                                {formatCurrency(inv.total, inv.currency)}
                                            </p>
                                            {inv.paidAmount > 0 && inv.paidAmount < inv.total && (
                                                <p className="text-[10px] text-emerald-400 font-bold">
                                                    Pagado: {formatCurrency(inv.paidAmount, inv.currency)}
                                                </p>
                                            )}
                                        </div>

                                        {isExpanded ? <ChevronDown className={`w-4 h-4 ${t.textMuted}`} /> : <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />}
                                    </button>

                                    {isExpanded && (
                                        <div className={`px-5 pb-5 border-t ${t.divider}`}>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 mb-4">
                                                <div className={`${t.innerBg} p-3 rounded-xl`}>
                                                    <p className={`text-[9px] ${t.textMuted} font-bold uppercase`}>Subtotal</p>
                                                    <p className={`text-sm font-bold ${t.textWhite} font-mono`}>{formatCurrency(inv.subtotal, inv.currency)}</p>
                                                </div>
                                                <div className={`${t.innerBg} p-3 rounded-xl`}>
                                                    <p className={`text-[9px] ${t.textMuted} font-bold uppercase`}>IVA</p>
                                                    <p className={`text-sm font-bold ${t.textWhite} font-mono`}>{formatCurrency(inv.tax, inv.currency)}</p>
                                                </div>
                                                <div className={`${t.innerBg} p-3 rounded-xl`}>
                                                    <p className={`text-[9px] ${t.textMuted} font-bold uppercase`}>Vencimiento</p>
                                                    <p className={`text-sm font-bold ${inv.dueDate < now && inv.status !== 'paid' ? 'text-red-400' : t.textWhite}`}>{formatDate(inv.dueDate)}</p>
                                                </div>
                                                <div className={`${t.innerBg} p-3 rounded-xl`}>
                                                    <p className={`text-[9px] ${t.textMuted} font-bold uppercase`}>Pagado</p>
                                                    <p className={`text-sm font-bold text-emerald-400 font-mono`}>{formatCurrency(inv.paidAmount || 0, inv.currency)}</p>
                                                </div>
                                            </div>

                                            {/* Payments for this invoice */}
                                            {invoicePayments.length > 0 && (
                                                <div>
                                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mb-2`}>Pagos Registrados</p>
                                                    {invoicePayments.map((pay: any) => (
                                                        <div key={pay.id} className={`flex items-center gap-3 p-3 ${t.innerBg} rounded-xl mb-2`}>
                                                            <CreditCard className={`w-4 h-4 text-emerald-400 flex-shrink-0`} />
                                                            <div className="flex-1">
                                                                <p className={`text-sm font-bold ${t.textWhite}`}>{formatCurrency(pay.amount, pay.currency)}</p>
                                                                <p className={`text-[10px] ${t.textMuted}`}>{formatDate(pay.paymentDate)} • {pay.method}</p>
                                                            </div>
                                                            {pay.reference && (
                                                                <p className={`text-[10px] font-mono ${t.textMuted}`}>{pay.reference}</p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {inv.notes && (
                                                <p className={`text-xs ${t.textMuted} mt-3 italic`}>📝 {inv.notes}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* ── PAYMENTS ── */}
            {activeView === 'payments' && (
                <div className="space-y-4 animate-fade-in">
                    {payments.length === 0 ? (
                        <div className={`${cardClass} text-center py-12`}>
                            <CreditCard className={`w-10 h-10 ${t.textMuted} mx-auto mb-3`} />
                            <p className={`text-sm font-bold ${t.textWhite}`}>Sin pagos registrados</p>
                            <p className={`text-xs ${t.textMuted} mt-1`}>Los pagos procesados aparecerán aquí</p>
                        </div>
                    ) : (
                        <div className={cardClass}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                                <CreditCard className="w-4 h-4 text-emerald-400" /> Historial de Pagos
                            </h3>
                            <div className="space-y-2">
                                {payments.map((pay: any) => (
                                    <div key={pay.id} className={`flex items-center gap-4 p-4 ${t.innerBg} rounded-xl`}>
                                        <div className={`p-2 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                                            <CheckCircle2 className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-bold ${t.textWhite}`}>
                                                {formatCurrency(pay.amount, pay.currency)}
                                            </p>
                                            <p className={`text-xs ${t.textMuted}`}>
                                                {formatDate(pay.paymentDate)} • Factura {pay.invoiceNumber}
                                                {pay.periodLabel ? ` (${pay.periodLabel})` : ''}
                                            </p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase`}>{pay.method}</p>
                                            {pay.reference && (
                                                <p className={`text-[10px] font-mono ${t.textMuted} mt-0.5`}>{pay.reference}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Payment total summary */}
                            <div className={`mt-4 pt-4 border-t ${t.divider} flex items-center justify-between`}>
                                <p className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest`}>Total Pagado</p>
                                <p className={`text-xl font-black text-emerald-400 font-mono`}>
                                    {formatCurrency(balance.totalPaid, currency)}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientBillingView;
